export type DateInput = string | number | Date | null | undefined;

const DATE_ONLY_REGEX = /^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/;

function toInt(value: string): number {
  return Number.parseInt(value, 10);
}

function buildLocalDate(year: number, month: number, day: number): Date | null {
  const candidate = new Date(year, month - 1, day, 0, 0, 0, 0);
  if (
    candidate.getFullYear() !== year
    || candidate.getMonth() !== month - 1
    || candidate.getDate() !== day
  ) {
    return null;
  }
  return candidate;
}

/**
 * Parses common date representations used in this codebase.
 *
 * Rules:
 * - Date-only strings (`YYYY-MM-DD`, `YYYY/MM/DD`) are interpreted as local dates.
 * - ISO date-time strings keep their explicit offset/UTC semantics.
 */
export function parseDateInput(value: DateInput): Date | null {
  if (value == null) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? new Date(value.getTime()) : null;
  }

  if (typeof value === 'number') {
    const fromEpoch = new Date(value);
    return Number.isFinite(fromEpoch.getTime()) ? fromEpoch : null;
  }

  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const dateOnlyMatch = DATE_ONLY_REGEX.exec(trimmed);
  if (dateOnlyMatch) {
    const year = toInt(dateOnlyMatch[1]);
    const month = toInt(dateOnlyMatch[2]);
    const day = toInt(dateOnlyMatch[3]);
    return buildLocalDate(year, month, day);
  }

  const parsed = new Date(trimmed);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
}

/**
 * Formats a date into ISO8601 with local offset (e.g. `2026-03-08T12:34:56.789+09:00`).
 */
export function toIsoOffsetString(value: DateInput): string | null {
  const date = parseDateInput(value);
  if (!date) {
    return null;
  }

  const pad2 = (n: number): string => String(n).padStart(2, '0');
  const pad3 = (n: number): string => String(n).padStart(3, '0');
  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absOffset = Math.abs(offsetMinutes);
  const offsetHours = pad2(Math.floor(absOffset / 60));
  const offsetMins = pad2(absOffset % 60);

  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`
    + `T${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}.${pad3(date.getMilliseconds())}`
    + `${sign}${offsetHours}:${offsetMins}`;
}

export function getTimestamp(value: DateInput): number | null {
  const parsed = parseDateInput(value);
  if (!parsed) {
    return null;
  }
  return parsed.getTime();
}
